'use client';

/**
 * V2Header - prostaysites.com marketing header.
 *
 * Much simpler than the prostay.com mega-menu header: this site has four
 * destinations, so the nav is a visible inline row on desktop and a small
 * dropdown panel on mobile. Same visual language (sticky bar, blur on
 * scroll, black pill CTA) so the two properties feel related.
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, X as CloseIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
/* Plain next/link for the language-switcher chips only — see
 * useLocaleSwitcher comments. The locale-aware Link mangles EN hrefs
 * into `/en/...` from non-EN pages and creates a 308 redirect chain. */
import NextLink from 'next/link';
import V2Logo from './V2Logo';
import { useLocaleSwitcher } from '@/components/v2/useLocaleSwitcher';

const NAV_LINKS = [
  { href: '/templates/', key: 'templates' },
  { href: '/features/',  key: 'features' },
  { href: '/pricing/',   key: 'pricing' }
];

export default function V2Header() {
  const t = useTranslations('Layout.Header');
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close the mobile panel on navigation. */
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  /* Lock body scroll while the mobile panel is open. */
  useEffect(() => {
    document.documentElement.style.overflow = open ? 'hidden' : '';
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled || open ? 'bg-white/85 backdrop-blur-xl' : 'bg-transparent'
        }`}
      >
        <div className="container-page">
          <div className="flex h-20 items-center justify-between gap-6">
            <Link
              href="/"
              aria-label={t('cta.homeAria')}
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              <V2Logo className="h-7 text-gray-950" />
            </Link>

            <nav
              aria-label={t('navAria')}
              className="hidden items-center gap-8 md:flex"
            >
              {NAV_LINKS.map((link) => {
                const active =
                  pathname === link.href ||
                  pathname === link.href.replace(/\/$/, '');
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    aria-current={active ? 'page' : undefined}
                    className={`text-sm font-medium tracking-tight transition-colors ${
                      active
                        ? 'text-gray-950'
                        : 'text-gray-600 hover:text-gray-950'
                    }`}
                  >
                    {t(`links.${link.key}`)}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <LocaleMenu />

              <Link
                href="/request-demo/"
                onClick={() => setOpen(false)}
                className="group relative hidden h-12 items-center gap-2 overflow-hidden rounded-full bg-gray-950 px-6 text-sm font-semibold tracking-tight text-white transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99] sm:inline-flex"
              >
                {t('cta.bookDemo')}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>

              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? t('cta.closeMenu') : t('cta.openMenu')}
                aria-expanded={open}
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 text-gray-950 transition-all duration-200 hover:border-gray-950 hover:bg-gray-950 hover:text-white md:hidden"
              >
                {open ? (
                  <CloseIcon className="h-5 w-5" />
                ) : (
                  <span className="flex flex-col gap-1.5">
                    <span className="block h-px w-5 bg-current" />
                    <span className="block h-px w-5 bg-current" />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile panel */}
      <div
        inert={!open ? '' : undefined}
        className={`fixed inset-0 top-20 z-30 bg-white transition-all duration-300 ease-out md:hidden ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="container-page flex h-full flex-col gap-2 overflow-y-auto pt-8 pb-16">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="border-b border-gray-100 py-5 text-2xl font-semibold tracking-tight text-gray-950"
            >
              {t(`links.${link.key}`)}
            </Link>
          ))}
          <Link
            href="/request-demo/"
            onClick={() => setOpen(false)}
            className="mt-8 inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gray-950 px-8 text-base font-semibold tracking-tight text-white"
          >
            {t('cta.bookDemo')}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <MobileLocaleChips onSelect={() => setOpen(false)} />
        </div>
      </div>
    </>
  );
}

/* Compact desktop language switcher: a small uppercase button that expands
 * into a chip row. Rendered as real anchors for crawlability. */
function LocaleMenu() {
  const t = useTranslations('Layout.Header');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const locales = useLocaleSwitcher({ onSelect: () => setOpen(false) });
  const active = locales.find((l) => l.active);

  /* Dismiss on outside click / tap and on Escape. */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t('languageAria')}
        className="inline-flex h-12 items-center gap-1.5 rounded-full px-3 text-[12px] font-bold uppercase tracking-[0.14em] text-gray-600 transition-colors hover:text-gray-950"
      >
        {active?.code}
        <svg viewBox="0 0 12 12" className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden>
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="absolute right-0 mt-2 w-44 rounded-2xl border border-gray-200 bg-white p-2 shadow-ui-md">
          {locales.map((opt) => (
            <li key={opt.code}>
              {opt.active ? (
                <span
                  aria-current="page"
                  className="flex items-center justify-between rounded-xl bg-gray-950 px-3 py-2 text-sm font-medium text-white"
                >
                  {opt.label}
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em]">{opt.code}</span>
                </span>
              ) : (
                <NextLink
                  href={opt.href}
                  hrefLang={opt.code}
                  onClick={opt.onSelect}
                  className="flex items-center justify-between rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950"
                >
                  {opt.label}
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-60">{opt.code}</span>
                </NextLink>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MobileLocaleChips({ onSelect }) {
  const locales = useLocaleSwitcher({ onSelect });
  return (
    <ul className="mt-10 flex flex-wrap gap-2">
      {locales.map((opt) => {
        const base =
          'inline-flex h-9 items-center rounded-full border px-3 text-[11px] font-bold uppercase tracking-[0.16em]';
        return (
          <li key={opt.code}>
            {opt.active ? (
              <span aria-current="page" className={`${base} border-gray-950 bg-gray-950 text-white`}>
                {opt.code}
              </span>
            ) : (
              <NextLink
                href={opt.href}
                hrefLang={opt.code}
                onClick={opt.onSelect}
                className={`${base} border-gray-200 text-gray-600 hover:border-gray-950 hover:text-gray-950`}
              >
                {opt.code}
              </NextLink>
            )}
          </li>
        );
      })}
    </ul>
  );
}
